import { useParams, Link } from 'react-router-dom';
import PublicLayout, { SEOHead, TrustBadges, CTASection, InternalLinks } from '../../components/PublicLayout';
import { SERVICES, LANGUAGES, US_CITIES, UK_CITIES, CA_CITIES, AU_CITIES, toSlug, getRelatedCities, getRelatedLanguages, getRelatedServices, getCityCountry } from '../../data/seo-data';
import { getServiceLocationImages } from '../../data/seo-images';
import { CheckCircle } from 'lucide-react';

export default function ServiceLocationPage() {
  const { serviceSlug, citySlug } = useParams();
  const service = SERVICES.find(s => s.slug === serviceSlug) || { name: serviceSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '), slug: serviceSlug };
  const allCities = [...US_CITIES, ...UK_CITIES, ...CA_CITIES, ...AU_CITIES];
  const city = allCities.find(c => toSlug(c) === citySlug) || citySlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  const country = getCityCountry(city);
  const images = getServiceLocationImages(serviceSlug, citySlug);

  return (
    <PublicLayout>
      <SEOHead
        title={`${service.name} in ${city} | Interpreter IO`}
        description={`Professional ${service.name.toLowerCase()} services in ${city}. Connect with qualified interpreters 24/7 in 250+ languages.`}
        canonical={`https://interpreterservices.us/services/${serviceSlug}/${citySlug}`}
        schema={{ "@context": "https://schema.org", "@type": "Service", name: `${service.name} in ${city}`, provider: { "@type": "Organization", name: "Interpreter Services", url: "https://interpreterservices.us" }, areaServed: { "@type": "City", name: city } }}
      />

      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-blue-300 text-sm mb-3"><Link to={`/services/${serviceSlug}`} className="hover:underline">{service.name}</Link> / <Link to={`/interpreting-services-${citySlug}`} className="hover:underline">{city}</Link></p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">{service.name} in {city}</h1>
          <p className="text-lg text-blue-100 max-w-3xl mb-8">Interpreter IO provides professional {service.name.toLowerCase()} services in {city}, {country}. Connect with qualified interpreters in over 250 languages, available 24 hours a day, 7 days a week.</p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact"><button className="bg-white text-blue-700 hover:bg-blue-50 font-semibold px-8 py-3 rounded-lg">Get Interpreter in {city}</button></Link>
            <Link to="/contact"><button className="border border-white text-white hover:bg-white/10 font-semibold px-8 py-3 rounded-lg">Request Pricing</button></Link>
          </div>
        </div>
      </section>

      <TrustBadges />

      <div className="max-w-4xl mx-auto px-6 py-16">
        <img src={images[0].url} alt={`${service.name} in ${city} — ${images[0].alt}`} className="w-full rounded-xl mb-10 object-cover h-56 md:h-72" loading="lazy" data-testid="svcl-img-1" />
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">About {service.name} in {city}</h2>
          <p className="text-slate-700 leading-relaxed mb-4">
            Organizations across {city} rely on {service.name.toLowerCase()} services for healthcare, legal, government, and corporate communication needs. Interpreter IO delivers fast, professional {service.name.toLowerCase()} with qualified interpreters in over 250 languages.
          </p>
          <p className="text-slate-700 leading-relaxed">
            Whether you are a hospital in {city} needing immediate language support for a patient, a law firm preparing for a hearing, or a business conducting a multilingual meeting, our {service.name.toLowerCase()} service connects you with the right interpreter instantly.
          </p>
        </section>

        <section className="mb-12">
          <img src={images[1].url} alt={`${service.name} quality in ${city} — ${images[1].alt}`} className="w-full rounded-xl mb-8 object-cover h-48 md:h-64" loading="lazy" data-testid="svcl-img-2" />
          <h2 className="text-2xl font-bold mb-4">Why Choose Interpreter IO in {city}?</h2>
          <ul className="space-y-3">
            {[
              `24/7 ${service.name.toLowerCase()} availability in ${city}`,
              '250+ languages including Spanish, Mandarin, Arabic, and ASL',
              'Professional human interpreters with industry specializations',
              'Typical connection time under 45 seconds',
              'HIPAA-compliant and secure platform',
            ].map(item => (
              <li key={item} className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" /><span className="text-slate-700">{item}</span></li>
            ))}
          </ul>
        </section>

        <section className="mb-12">
          <img src={images[2].url} alt={`Top languages for ${service.name.toLowerCase()} in ${city} — ${images[2].alt}`} className="w-full rounded-xl mb-8 object-cover h-48 md:h-64" loading="lazy" data-testid="svcl-img-3" />
          <h2 className="text-2xl font-bold mb-4">Top Languages for {service.name} in {city}</h2>
          <div className="flex flex-wrap gap-2">
            {LANGUAGES.slice(0, 12).map(l => (
              <Link key={l.slug} to={`/${l.slug}-interpreter-${citySlug}`} className="px-3 py-1.5 border rounded-lg text-sm hover:bg-blue-50">{l.name}</Link>
            ))}
          </div>
        </section>
        <img src={images[3].url} alt={images[3].alt} className="w-full rounded-xl mb-4 object-cover h-48 md:h-64" loading="lazy" data-testid="svcl-img-4" />
      </div>

      <CTASection />
      <InternalLinks cities={getRelatedCities(citySlug)} languages={getRelatedLanguages('', 3)} services={getRelatedServices(serviceSlug, 2)} />
    </PublicLayout>
  );
}
